import { RouteRecordRaw } from 'vue-router';
import { requireAuth, setDayjsTimezone } from '@/common/router/navigationGuard';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { extractAccessibleRoutes } from '@/common/auth/auth.utils';
import { VIEW_MODE } from '@/common/define/common.define';
import BaseViewport from '@/common/components/templates/baseViewport/BaseViewport.vue';
import { VIEWPORT_TYPE } from '@/common/components/templates/baseViewport/baseViewport.define';
import { requireDomainTargetOrTutorial, requireOpenShiftCluster } from '@/common/router/authGuard';

const PREFIX_KUBERNETES = 'kubernetes_';

// 토폴로지 두 화면은 서로의 경로로 이동하고, 언마운트 시 "토폴로지 간 이동"인지로 focus 클러스터 유지/초기화를
// 판정한다. 절대 경로를 한 곳에서 공유해 경로 개명 시 판정이 어긋나는 문제(문자열 중복·오타)를 막는다.
export const KUBERNETES_TOPOLOGY_PATH = {
  cluster: '/kubernetes/cluster-topology',
  object: '/kubernetes/object-topology',
} as const;

// K8s 화면은 모두 1depth. route는 flat(`/kubernetes/<screen>`)하게 안정적 per-screen path로 두고,
// GNB 그룹핑(그룹/라벨/순서/서브그룹)은 baseMenu의 K8S_GNB_GROUPS define이 담당한다(DSP-44218).
// 따라서 route parent를 그룹 단위로 나누지 않는다(그룹 재편/개명이 URL에 영향 없음).
const kubernetesMenu: RouteRecordRaw[] = [
  {
    path: '/kubernetes/',
    name: `${PREFIX_KUBERNETES}Kubernetes`,
    component: BaseViewport,
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('kubernetes'), setDayjsTimezone],
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES,
      invisibleViewMode: [VIEW_MODE.MAXGAUGE, VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.REALTIME,
    },
    children: [
      {
        path: 'cluster-topology',
        name: `${PREFIX_KUBERNETES}Cluster Topology`,
        component: () => import('@/kubernetes/views/clusterTopologyView/ClusterTopologyView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
        },
      },
      {
        path: 'object-topology',
        name: `${PREFIX_KUBERNETES}Object Topology`,
        component: () => import('@/kubernetes/views/objectTopologyView/ObjectTopologyView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INVENTORY,
        },
      },
      {
        path: 'cluster',
        name: `${PREFIX_KUBERNETES}Cluster`,
        component: () => import('../views/clusterView/ClusterView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
        },
      },
      {
        path: 'pod',
        name: `${PREFIX_KUBERNETES}Pod`,
        component: () => import('../views/podsListView/PodsListView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
        },
      },
      {
        path: 'container',
        name: `${PREFIX_KUBERNETES}Container`,
        component: () => import('../views/containerView/ContainerView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
        },
      },
      {
        path: 'namespace',
        name: `${PREFIX_KUBERNETES}Namespace`,
        component: () => import('../views/namespaceView/NamespaceView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
        },
      },
      {
        path: 'node',
        name: `${PREFIX_KUBERNETES}Node`,
        component: () => import('../views/nodeView/NodeView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INFRASTRUCTURE,
        },
      },
      {
        path: 'storage',
        name: `${PREFIX_KUBERNETES}Storage`,
        component: () => import('../views/storageOverview/StorageOverview.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_STORAGE,
        },
      },
      {
        path: 'cluster-resource-inventory',
        name: `${PREFIX_KUBERNETES}Cluster Resource Inventory`,
        component: () =>
          import('../views/clusterResourceInventoryView/ClusterResourceInventoryView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_INVENTORY,
        },
      },
      {
        path: 'workload',
        name: `${PREFIX_KUBERNETES}Workload`,
        component: () => import('../views/workloads/workloadsView/WorkloadsView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_WORKLOAD,
        },
      },
      {
        path: 'hpa',
        name: `${PREFIX_KUBERNETES}HPA`,
        component: () => import('../views/workloads/hpaView/HpaView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_SCALING,
        },
      },
      {
        path: 'job',
        name: `${PREFIX_KUBERNETES}Job`,
        component: () => import('../views/workloads/jobsView/JobsView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_WORKLOAD,
          resourceType: 'batch',
        },
      },
      {
        path: 'cron-job',
        name: `${PREFIX_KUBERNETES}CronJob`,
        component: () => import('../views/workloads/cronJobsView/CronJobsView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_WORKLOAD,
          resourceType: 'batch',
        },
      },
      {
        path: 'service-map',
        name: `${PREFIX_KUBERNETES}Service Map`,
        component: () => import('@/kubernetes/views/network/serviceMap/ServiceMapView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_NETWORK,
        },
      },
      {
        path: 'service',
        name: `${PREFIX_KUBERNETES}Service`,
        component: () => import('@/kubernetes/views/network/serviceView/ServiceView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_NETWORK,
        },
      },
      {
        path: 'ingress',
        name: `${PREFIX_KUBERNETES}Ingress`,
        component: () => import('@/kubernetes/views/network/ingressView/IngressView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_NETWORK,
        },
      },
      {
        path: 'ingress-controller',
        name: `${PREFIX_KUBERNETES}Ingress Controller`,
        component: () =>
          import('@/kubernetes/views/network/ingressControllerView/IngressControllerView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_NETWORK,
        },
      },
      {
        // OpenShift Route는 openshift 클러스터에서만 접근 가능 → 이 화면에만 추가 가드.
        path: 'route',
        name: `${PREFIX_KUBERNETES}Route`,
        component: () =>
          import('@/kubernetes/views/network/openshift/routeView/OpenshiftRouteView.vue'),
        beforeEnter: [requireOpenShiftCluster],
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_NETWORK,
        },
      },
      {
        path: 'config-map',
        name: `${PREFIX_KUBERNETES}Configmap`,
        component: () => import('../views/configStorage/configMapsView/ConfigMapsView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_CONFIGURATION,
        },
      },
      {
        path: 'persistent-volume-claim',
        name: `${PREFIX_KUBERNETES}Persistent Volume Claim`,
        component: () =>
          import('@/kubernetes/views/configStorage/persistentVolumesClaimsView/PersistentVolumesClaimsView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_STORAGE,
        },
      },
      {
        path: 'persistent-volume',
        name: `${PREFIX_KUBERNETES}Persistent Volume`,
        component: () =>
          import('../views/configStorage/persistentVolumesView/PersistentVolumesView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_STORAGE,
        },
      },
      {
        path: 'storage-class',
        name: `${PREFIX_KUBERNETES}Storage Classes`,
        component: () => import('../views/configStorage/storageClassesView/StorageClassesView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_STORAGE,
        },
      },
      {
        path: 'role',
        name: `${PREFIX_KUBERNETES}Role`,
        component: () => import('../views/accessControl/rolesView/RolesView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ACCESS_CONTROL,
        },
      },
      {
        path: 'role-binding',
        name: `${PREFIX_KUBERNETES}Role Binding`,
        component: () => import('../views/accessControl/rolesBindingsView/RolesBindingsView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ACCESS_CONTROL,
        },
      },
      {
        path: 'cluster-role',
        name: `${PREFIX_KUBERNETES}Cluster Role`,
        component: () => import('../views/accessControl/clusterRolesView/ClusterRolesView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ACCESS_CONTROL,
        },
      },
      {
        path: 'cluster-role-binding',
        name: `${PREFIX_KUBERNETES}Cluster Role Binding`,
        component: () =>
          import('../views/accessControl/clusterRolesBindingsView/ClusterRolesBindingsView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ACCESS_CONTROL,
        },
      },
      {
        path: 'service-account',
        name: `${PREFIX_KUBERNETES}ServiceAccount`,
        component: () =>
          import('../views/accessControl/serviceAccountsView/ServiceAccountsView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ACCESS_CONTROL,
        },
      },
    ],
  },
];

const getKubernetesNaviMenu = () => extractAccessibleRoutes(kubernetesMenu);

export { kubernetesMenu, getKubernetesNaviMenu };
